import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import DOMPurify from 'dompurify'
import React from 'react'
import { rtfListStyles } from '../rtf/rtfListStyles'
type RTFOutputProps = {
   text: string
   fontSize?: 300 | 400
   noMargin?: boolean
   ellipsis?: boolean
}

/**
 * Typography for the HTML produced by the back office TipTap editor.
 *
 * The styles are applied as descendant selectors rather than injected into the markup as
 * inline styles, so they also reach elements that carry attributes of their own — such as
 * the indented `<p style="margin-left: …">` and the lettered `<ol type="a">` the editor emits.
 *
 * Only vertical margins are ever set, never the `margin` shorthand: Griffel expands the
 * shorthand to all four longhands, which would compete with the indent.
 */
const useStyles = makeStyles({
   root: {
      fontWeight: tokens.fontWeightRegular,
      '& a': { color: tokens.colorBrandForegroundLink },
      // A heading can only have arrived by paste: the back office toolbar has never been able to
      // make one, and since #6465 its editor drops them on the way in. Content stored before that
      // still carries them, and a browser draws them bold and large — the "text looks bold" of the
      // ticket, on text no author can reach. Headings are therefore given the weight, and below the
      // size and margins, of a paragraph. Comma-separated keys carry an explicit `&` on every
      // alternative, for the reason spelled out in rtfListStyles.ts.
      //
      // MIRRORED RULE. The same four rules live in 365-back-office-web's RTFOutput; a change here
      // has to be made there too, or the author's view and the reader's drift apart.
      '& h1, & h2, & h3, & h4, & h5, & h6': { fontWeight: tokens.fontWeightRegular },
      // TipTap serialises list items as <li><p>…</p></li>; without this every list item
      // would be a full blank line tall.
      '& li > p': { marginTop: 0, marginBottom: 0 },
      '& li > ul': { marginTop: 0, marginBottom: 0 },
      '& li > ol': { marginTop: 0, marginBottom: 0 },
   },
   /**
    * Word's multilevel list markers and indent steps. Kept in its own slot so that no
    * property is ever declared both here and in `root`: these rules own the horizontal
    * axis (`padding-inline-start`, `list-style-*`) while `root` owns the vertical rhythm
    * (`margin-top`/`margin-bottom`), so the two can never compete.
    */
   lists: rtfListStyles,

   size300: {
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
      '& p': { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
      '& li': { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
      '& h1, & h2, & h3, & h4, & h5, & h6': { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
   },
   size400: {
      fontSize: tokens.fontSizeBase400,
      lineHeight: tokens.lineHeightBase400,
      '& p': { fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400 },
      '& li': { fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400 },
      '& h1, & h2, & h3, & h4, & h5, & h6': { fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400 },
   },
   noMargin: {
      '& p': { marginTop: 0, marginBottom: 0 },
      '& li': { marginTop: 0, marginBottom: 0 },
      '& h1, & h2, & h3, & h4, & h5, & h6': { marginTop: 0, marginBottom: 0 },
   },
   ellipsis: {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'normal',
   },
})

export const RTFOutput = ({ text, fontSize, noMargin, ellipsis }: RTFOutputProps) => {
   const classes = useStyles()

   return (
      <div
         className={mergeClasses(
            classes.root,
            classes.lists,
            fontSize === 300 ? classes.size300 : classes.size400,
            noMargin ? classes.noMargin : undefined,
            ellipsis ? classes.ellipsis : undefined
         )}
         dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text ?? '') }}
      />
   )
}
